import { useState } from 'react';
import NavBar from '../components/NavBar';
import { getDesignTokens } from '../utils/designTokens';
import { useThemeStore } from '../store/themeStore';
import { Icon } from '../utils/icons';
import MemberAvatar from '../components/MemberAvatar';
import { EventMember } from '../types/calendar';
import { Todo, TodoStatus, TodoPriority } from '../types/todo';
import BottomSheet from '../components/mobile/BottomSheet';

// Mock data
const mockMembers: EventMember[] = [
  { memberId: '1', memberName: 'Emma', memberColor: '#3b82f6' },
  { memberId: '2', memberName: 'Lucas', memberColor: '#ef4444' },
  { memberId: '3', memberName: 'Mom', memberColor: '#10b981' },
  { memberId: '4', memberName: 'Dad', memberColor: '#8b5cf6' },
];

const sampleTodos: Todo[] = [
  {
    todoId: '1',
    familyId: 'family-1',
    title: 'Buy groceries for dinner',
    description: 'Milk, eggs, bread, chicken',
    status: 'open',
    priority: 'high',
    dueDateTime: new Date(),
    assigneeId: '1',
    assigneeName: 'Emma',
    isFamily: false,
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    todoId: '2',
    familyId: 'family-1',
    title: 'Call dentist to schedule appointment',
    status: 'open',
    priority: 'medium',
    dueDateTime: new Date(Date.now() + 86400000),
    assigneeId: '3',
    assigneeName: 'Mom',
    isFamily: false,
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    todoId: '3',
    familyId: 'family-1',
    title: 'Finish homework assignment',
    description: 'Math chapter 5, problems 1-20',
    status: 'open',
    priority: 'critical',
    dueDateTime: new Date(Date.now() - 86400000),
    assigneeId: '2',
    assigneeName: 'Lucas',
    isFamily: false,
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    todoId: '4',
    familyId: 'family-1',
    title: 'Plan weekend family activity',
    status: 'open',
    priority: 'low',
    assigneeId: undefined,
    assigneeName: undefined,
    isFamily: true,
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    todoId: '5',
    familyId: 'family-1',
    title: 'Pick up dry cleaning',
    status: 'done',
    priority: 'medium',
    dueDateTime: new Date(Date.now() - 172800000),
    assigneeId: '4',
    assigneeName: 'Dad',
    isFamily: false,
    createdAt: new Date(),
    updatedAt: new Date(),
  },
];

const mockLists = [
  { listId: '1', name: 'Shopping', theme: 'Groceries', icon: '🛒' },
  { listId: '2', name: 'Work', theme: 'Office', icon: '💼' },
  { listId: '3', name: 'Home', theme: 'Chores', icon: '🏠' },
];

// Proposal 1: Tabbed Interface with Quick Add
function Proposal1FullUI({ isDarkMode }: { isDarkMode: boolean }) {
  const tokens = getDesignTokens(isDarkMode);
  const [activeTab, setActiveTab] = useState<'todos' | 'lists'>('todos');
  const [scope, setScope] = useState<'my' | 'family'>('family');
  const [statusFilter, setStatusFilter] = useState<TodoStatus | 'all'>('open');
  const [priorityFilter, setPriorityFilter] = useState<TodoPriority | 'all'>('all');
  const [assigneeFilter, setAssigneeFilter] = useState<string | 'all'>('all');
  const [sortBy, setSortBy] = useState<'due' | 'priority' | 'title'>('due');
  const [showFilters, setShowFilters] = useState(false);
  const [quickAddTitle, setQuickAddTitle] = useState('');
  const [quickAddDue, setQuickAddDue] = useState('');
  const [quickAddPriority, setQuickAddPriority] = useState<TodoPriority>('medium');

  return (
    <div className="h-full flex flex-col overflow-hidden" style={{ maxHeight: '700px' }}>
      {/* Header with Tabs */}
      <div className={`sticky top-0 ${tokens.card.bg} ${tokens.border.light} border-b z-10`}>
        <div className="px-4 py-3">
          <div className="flex items-center justify-between mb-3">
            <h1 className={`text-2xl font-bold ${tokens.text.primary}`}>To Dos</h1>
            <button
              className={`p-2 rounded-landing ${tokens.button.secondary} min-h-[44px] min-w-[44px] flex items-center justify-center`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <Icon name="filter" size="sm" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-3">
            <button
              className={`flex-1 px-4 py-2 rounded-landing text-sm font-semibold border min-h-[44px] transition-colors ${
                activeTab === 'todos'
                  ? `${tokens.button.primary} border-transparent text-white`
                  : `${tokens.card.bg} ${tokens.text.primary} ${tokens.border.light}`
              }`}
              onClick={() => setActiveTab('todos')}
            >
              Todos
            </button>
            <button
              className={`flex-1 px-4 py-2 rounded-landing text-sm font-semibold border min-h-[44px] transition-colors ${
                activeTab === 'lists'
                  ? `${tokens.button.primary} border-transparent text-white`
                  : `${tokens.card.bg} ${tokens.text.primary} ${tokens.border.light}`
              }`}
              onClick={() => setActiveTab('lists')}
            >
              Lists
            </button>
          </div>

          {/* Quick Add (Desktop Feature) */}
          {activeTab === 'todos' && (
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                className={`flex-1 px-3 py-2 rounded-landing text-sm ${tokens.border.light} border ${tokens.card.bg} ${tokens.text.primary} min-h-[44px]`}
                placeholder="Quick add todo..."
                value={quickAddTitle}
                onChange={(e) => setQuickAddTitle(e.target.value)}
              />
              <input
                type="date"
                className={`px-3 py-2 rounded-landing text-sm ${tokens.border.light} border ${tokens.card.bg} ${tokens.text.primary} min-h-[44px]`}
                value={quickAddDue}
                onChange={(e) => setQuickAddDue(e.target.value)}
              />
              <select
                className={`px-3 py-2 rounded-landing text-sm ${tokens.border.light} border ${tokens.card.bg} ${tokens.text.primary} min-h-[44px]`}
                value={quickAddPriority}
                onChange={(e) => setQuickAddPriority(e.target.value as TodoPriority)}
              >
                <option value="low">Low</option>
                <option value="medium">Med</option>
                <option value="high">High</option>
                <option value="critical">Crit</option>
              </select>
              <button
                className={`px-4 py-2 rounded-landing ${tokens.button.primary} text-white font-semibold min-h-[44px] min-w-[60px]`}
                disabled={!quickAddTitle.trim()}
              >
                Add
              </button>
            </div>
          )}

          {/* Scope Filter */}
          <div className="flex gap-2 mb-2">
            <button
              className={`flex-1 px-3 py-2 rounded-landing text-sm font-semibold border min-h-[44px] ${
                scope === 'family' ? tokens.button.primary : tokens.card.bg
              }`}
              onClick={() => setScope('family')}
            >
              Family
            </button>
            <button
              className={`flex-1 px-3 py-2 rounded-landing text-sm font-semibold border min-h-[44px] ${
                scope === 'my' ? tokens.button.primary : tokens.card.bg
              }`}
              onClick={() => setScope('my')}
            >
              My
            </button>
          </div>

          {/* Expandable Filters */}
          {showFilters && (
            <div className="space-y-2 mb-2 p-3 rounded-landing" style={{ backgroundColor: tokens.bg.secondary }}>
              {/* Status Filter */}
              <div>
                <label className={`text-xs font-semibold ${tokens.text.secondary} mb-1 block`}>Status</label>
                <div className="flex gap-1">
                  {(['open', 'done', 'all'] as const).map((filter) => (
                    <button
                      key={filter}
                      className={`flex-1 px-2 py-1.5 rounded-landing text-xs font-medium min-h-[36px] ${
                        statusFilter === filter ? tokens.button.secondary : tokens.card.bg
                      }`}
                      onClick={() => setStatusFilter(filter)}
                    >
                      {filter}
                    </button>
                  ))}
                </div>
              </div>

              {/* Priority Filter */}
              <div>
                <label className={`text-xs font-semibold ${tokens.text.secondary} mb-1 block`}>Priority</label>
                <div className="flex gap-1">
                  {(['all', 'critical', 'high', 'medium', 'low'] as const).map((filter) => (
                    <button
                      key={filter}
                      className={`px-2 py-1.5 rounded-landing text-xs font-medium min-h-[36px] ${
                        priorityFilter === filter ? tokens.button.secondary : tokens.card.bg
                      }`}
                      onClick={() => setPriorityFilter(filter)}
                    >
                      {filter === 'all' ? 'All' : filter.charAt(0).toUpperCase() + filter.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Assignee Filter */}
              <div>
                <label className={`text-xs font-semibold ${tokens.text.secondary} mb-1 block`}>Assignee</label>
                <div className="flex gap-1 flex-wrap">
                  <button
                    className={`px-2 py-1.5 rounded-landing text-xs font-medium min-h-[36px] ${
                      assigneeFilter === 'all' ? tokens.button.secondary : tokens.card.bg
                    }`}
                    onClick={() => setAssigneeFilter('all')}
                  >
                    All
                  </button>
                  {mockMembers.map((member) => (
                    <button
                      key={member.memberId}
                      className={`px-2 py-1.5 rounded-landing text-xs font-medium min-h-[36px] flex items-center gap-1 ${
                        assigneeFilter === member.memberId ? tokens.button.secondary : tokens.card.bg
                      }`}
                      onClick={() => setAssigneeFilter(member.memberId)}
                    >
                      <MemberAvatar member={member} size="sm" />
                      {member.memberName}
                    </button>
                  ))}
                </div>
              </div>

              {/* Sort */}
              <div>
                <label className={`text-xs font-semibold ${tokens.text.secondary} mb-1 block`}>Sort By</label>
                <div className="flex gap-1">
                  {(['due', 'priority', 'title'] as const).map((option) => (
                    <button
                      key={option}
                      className={`flex-1 px-2 py-1.5 rounded-landing text-xs font-medium min-h-[36px] ${
                        sortBy === option ? tokens.button.secondary : tokens.card.bg
                      }`}
                      onClick={() => setSortBy(option)}
                    >
                      {option.charAt(0).toUpperCase() + option.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-3 py-2">
        {activeTab === 'todos' ? (
          <div className="space-y-2">
            {sampleTodos.map(todo => (
              <div
                key={todo.todoId}
                className={`${tokens.card.bg} ${tokens.border.light} border rounded-landing p-3 min-h-[64px]`}
                style={{ borderLeftWidth: '4px', borderLeftColor: todo.priority === 'critical' ? '#ef4444' : '#3b82f6' }}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    todo.status === 'done' ? 'bg-green-500 border-green-500' : 'border-gray-300'
                  }`}>
                    {todo.status === 'done' && <Icon name="check" size="sm" className="text-white" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-sm font-semibold ${todo.status === 'done' ? 'line-through text-gray-500' : tokens.text.primary}`}>
                      {todo.title}
                    </h3>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {todo.dueDateTime && <span className={`text-xs ${tokens.text.muted}`}>Today</span>}
                      {todo.assigneeName && <MemberAvatar member={mockMembers.find(m => m.memberName === todo.assigneeName) || mockMembers[0]} size="sm" />}
                      {todo.priority && todo.priority !== 'low' && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          todo.priority === 'critical' ? 'bg-red-100 text-red-700' :
                          todo.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {todo.priority}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {mockLists.map(list => (
              <div key={list.listId} className={`${tokens.card.bg} ${tokens.border.light} border rounded-landing p-3 min-h-[60px] flex items-center justify-between`}>
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{list.icon}</span>
                  <div>
                    <h3 className={`text-sm font-semibold ${tokens.text.primary}`}>{list.name}</h3>
                    <p className={`text-xs ${tokens.text.muted}`}>{list.theme}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button className={`p-2 rounded-landing ${tokens.button.secondary} min-h-[44px] min-w-[44px]`}>
                    <Icon name="edit" size="sm" />
                  </button>
                  <button className={`p-2 rounded-landing bg-red-100 text-red-700 min-h-[44px] min-w-[44px]`}>
                    <Icon name="delete" size="sm" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Proposal 2: Drawer-Based Filters with Compact View
function Proposal2FullUI({ isDarkMode }: { isDarkMode: boolean }) {
  const tokens = getDesignTokens(isDarkMode);
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const [viewMode, setViewMode] = useState<'cards' | 'compact'>('cards');
  const [scope, setScope] = useState<'my' | 'family'>('family');
  const [statusFilter, setStatusFilter] = useState<TodoStatus | 'all'>('open');
  const [priorityFilter, setPriorityFilter] = useState<TodoPriority | 'all'>('all');
  const [sortBy, setSortBy] = useState<'due' | 'priority' | 'title'>('due');

  return (
    <div className="h-full flex flex-col overflow-hidden" style={{ maxHeight: '700px' }}>
      {/* Compact Header */}
      <div className={`sticky top-0 ${tokens.card.bg} ${tokens.border.light} border-b z-10`}>
        <div className="px-4 py-2">
          <div className="flex items-center justify-between mb-2">
            <h1 className={`text-xl font-bold ${tokens.text.primary}`}>To Dos</h1>
            <div className="flex items-center gap-2">
              <button
                className={`p-2 rounded-landing ${tokens.button.secondary} min-h-[44px] min-w-[44px]`}
                onClick={() => setViewMode(viewMode === 'cards' ? 'compact' : 'cards')}
              >
                <Icon name={viewMode === 'cards' ? 'menu' : 'checklist'} size="sm" />
              </button>
              <button
                className={`p-2 rounded-landing ${tokens.button.secondary} min-h-[44px] min-w-[44px]`}
                onClick={() => setShowFilterDrawer(true)}
              >
                <Icon name="filter" size="sm" />
              </button>
              <button
                className={`p-2 rounded-landing ${tokens.button.primary} min-h-[44px] min-w-[44px]`}
              >
                <Icon name="plus" size="sm" className="text-white" />
              </button>
            </div>
          </div>

          {/* Quick Scope & Status */}
          <div className="flex gap-1">
            <button
              className={`px-3 py-1.5 rounded-landing text-xs font-semibold min-h-[36px] ${
                scope === 'family' ? tokens.button.primary : tokens.card.bg
              }`}
              onClick={() => setScope('family')}
            >
              Family
            </button>
            <button
              className={`px-3 py-1.5 rounded-landing text-xs font-semibold min-h-[36px] ${
                scope === 'my' ? tokens.button.primary : tokens.card.bg
              }`}
              onClick={() => setScope('my')}
            >
              My
            </button>
            <div className="flex-1"></div>
            {(['open', 'done', 'all'] as const).map((filter) => (
              <button
                key={filter}
                className={`px-2 py-1.5 rounded-full text-xs font-medium min-h-[36px] ${
                  statusFilter === filter ? tokens.button.secondary : tokens.card.bg
                }`}
                onClick={() => setStatusFilter(filter)}
              >
                {filter}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-3 py-2">
        {viewMode === 'cards' ? (
          <div className="space-y-2">
            {sampleTodos.map(todo => (
              <div
                key={todo.todoId}
                className={`${tokens.card.bg} ${tokens.border.light} border rounded-landing p-4 min-h-[80px]`}
                style={{ borderLeftWidth: '4px', borderLeftColor: todo.priority === 'critical' ? '#ef4444' : '#3b82f6' }}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    todo.status === 'done' ? 'bg-green-500 border-green-500' : 'border-gray-300'
                  }`}>
                    {todo.status === 'done' && <Icon name="check" size="sm" className="text-white" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-base font-semibold ${todo.status === 'done' ? 'line-through text-gray-500' : tokens.text.primary}`}>
                      {todo.title}
                    </h3>
                    {todo.description && (
                      <p className={`text-sm ${tokens.text.muted} mt-1`}>{todo.description}</p>
                    )}
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      {todo.dueDateTime && <span className={`text-xs ${tokens.text.muted}`}>Today</span>}
                      {todo.assigneeName && <MemberAvatar member={mockMembers.find(m => m.memberName === todo.assigneeName) || mockMembers[0]} size="sm" />}
                      {todo.priority && todo.priority !== 'low' && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          todo.priority === 'critical' ? 'bg-red-100 text-red-700' :
                          todo.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {todo.priority}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-1">
            {sampleTodos.map(todo => (
              <div
                key={todo.todoId}
                className={`${tokens.card.bg} ${tokens.border.light} border-l-4 rounded-landing p-2 min-h-[48px] flex items-center gap-2`}
                style={{ borderLeftColor: todo.priority === 'critical' ? '#ef4444' : '#3b82f6' }}
              >
                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                  todo.status === 'done' ? 'bg-green-500 border-green-500' : 'border-gray-300'
                }`}>
                  {todo.status === 'done' && <Icon name="check" size="sm" className="text-white" />}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className={`text-xs font-semibold ${todo.status === 'done' ? 'line-through text-gray-500' : tokens.text.primary}`}>
                    {todo.title}
                  </h3>
                </div>
                {todo.priority && todo.priority !== 'low' && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                    todo.priority === 'critical' ? 'bg-red-100 text-red-700' :
                    todo.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                    'bg-yellow-100 text-yellow-700'
                  }`}>
                    {todo.priority.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Filter Drawer (Bottom Sheet) */}
      <BottomSheet
        isOpen={showFilterDrawer}
        onClose={() => setShowFilterDrawer(false)}
        title="Filters & Sort"
      >
        <div className="p-4 space-y-4">
          <div>
            <label className={`text-sm font-semibold ${tokens.text.primary} mb-2 block`}>Priority</label>
            <div className="flex gap-2 flex-wrap">
              {(['all', 'critical', 'high', 'medium', 'low'] as const).map((filter) => (
                <button
                  key={filter}
                  className={`px-3 py-2 rounded-landing text-sm font-medium min-h-[44px] ${
                    priorityFilter === filter ? tokens.button.primary : tokens.button.secondary
                  }`}
                  onClick={() => setPriorityFilter(filter)}
                >
                  {filter === 'all' ? 'All' : filter.charAt(0).toUpperCase() + filter.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className={`text-sm font-semibold ${tokens.text.primary} mb-2 block`}>Sort By</label>
            <div className="flex gap-2">
              {(['due', 'priority', 'title'] as const).map((option) => (
                <button
                  key={option}
                  className={`flex-1 px-3 py-2 rounded-landing text-sm font-medium min-h-[44px] ${
                    sortBy === option ? tokens.button.primary : tokens.button.secondary
                  }`}
                  onClick={() => setSortBy(option)}
                >
                  {option.charAt(0).toUpperCase() + option.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>
      </BottomSheet>
    </div>
  );
}

// Proposal 3: Swipeable Tabs with All Features
function Proposal3FullUI({ isDarkMode }: { isDarkMode: boolean }) {
  const tokens = getDesignTokens(isDarkMode);
  const [activeView, setActiveView] = useState<'todos' | 'lists' | 'quick-add'>('todos');
  const [scope, setScope] = useState<'my' | 'family'>('family');
  const [statusFilter, setStatusFilter] = useState<TodoStatus | 'all'>('open');
  const [priorityFilter, setPriorityFilter] = useState<TodoPriority | 'all'>('all');
  const [sortBy, setSortBy] = useState<'due' | 'priority' | 'title' | 'assignee' | 'list'>('due');

  return (
    <div className="h-full flex flex-col overflow-hidden" style={{ maxHeight: '700px' }}>
      {/* Header with Swipeable Tabs */}
      <div className={`sticky top-0 ${tokens.card.bg} ${tokens.border.light} border-b z-10`}>
        <div className="px-4 py-3">
          <h1 className={`text-2xl font-bold ${tokens.text.primary} mb-3`}>To Dos</h1>

          {/* Swipeable Tab Indicators */}
          <div className="flex gap-1 mb-3">
            {(['todos', 'lists', 'quick-add'] as const).map((view) => (
              <button
                key={view}
                className={`flex-1 px-3 py-2 rounded-landing text-sm font-semibold border min-h-[44px] transition-colors ${
                  activeView === view
                    ? `${tokens.button.primary} border-transparent text-white`
                    : `${tokens.card.bg} ${tokens.text.primary} ${tokens.border.light}`
                }`}
                onClick={() => setActiveView(view)}
              >
                {view === 'quick-add' ? 'Quick Add' : view.charAt(0).toUpperCase() + view.slice(1)}
              </button>
            ))}
          </div>

          {/* Context-Aware Controls */}
          {activeView === 'todos' && (
            <>
              <div className="flex gap-2 mb-2">
                <button
                  className={`flex-1 px-3 py-2 rounded-landing text-sm font-semibold border min-h-[44px] ${
                    scope === 'family' ? tokens.button.primary : tokens.card.bg
                  }`}
                  onClick={() => setScope('family')}
                >
                  Family
                </button>
                <button
                  className={`flex-1 px-3 py-2 rounded-landing text-sm font-semibold border min-h-[44px] ${
                    scope === 'my' ? tokens.button.primary : tokens.card.bg
                  }`}
                  onClick={() => setScope('my')}
                >
                  My
                </button>
              </div>
              <div className="flex gap-1">
                {(['open', 'done', 'all'] as const).map((filter) => (
                  <button
                    key={filter}
                    className={`flex-1 px-2 py-1.5 rounded-landing text-xs font-medium min-h-[36px] ${
                      statusFilter === filter ? tokens.button.secondary : tokens.card.bg
                    }`}
                    onClick={() => setStatusFilter(filter)}
                  >
                    {filter}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Content Based on Active View */}
      <div className="flex-1 overflow-y-auto px-3 py-2">
        {activeView === 'todos' && (
          <div className="space-y-2">
            {sampleTodos.map(todo => (
              <div
                key={todo.todoId}
                className={`${tokens.card.bg} ${tokens.border.light} border rounded-landing p-3 min-h-[64px]`}
                style={{ borderLeftWidth: '4px', borderLeftColor: todo.priority === 'critical' ? '#ef4444' : '#3b82f6' }}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    todo.status === 'done' ? 'bg-green-500 border-green-500' : 'border-gray-300'
                  }`}>
                    {todo.status === 'done' && <Icon name="check" size="sm" className="text-white" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-sm font-semibold ${todo.status === 'done' ? 'line-through text-gray-500' : tokens.text.primary}`}>
                      {todo.title}
                    </h3>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {todo.dueDateTime && <span className={`text-xs ${tokens.text.muted}`}>Today</span>}
                      {todo.assigneeName && <MemberAvatar member={mockMembers.find(m => m.memberName === todo.assigneeName) || mockMembers[0]} size="sm" />}
                      {todo.priority && todo.priority !== 'low' && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          todo.priority === 'critical' ? 'bg-red-100 text-red-700' :
                          todo.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {todo.priority}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeView === 'lists' && (
          <div className="space-y-2">
            {mockLists.map(list => (
              <div key={list.listId} className={`${tokens.card.bg} ${tokens.border.light} border rounded-landing p-3 min-h-[60px] flex items-center justify-between`}>
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{list.icon}</span>
                  <div>
                    <h3 className={`text-sm font-semibold ${tokens.text.primary}`}>{list.name}</h3>
                    <p className={`text-xs ${tokens.text.muted}`}>{list.theme}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button className={`p-2 rounded-landing ${tokens.button.secondary} min-h-[44px] min-w-[44px]`}>
                    <Icon name="edit" size="sm" />
                  </button>
                  <button className={`p-2 rounded-landing bg-red-100 text-red-700 min-h-[44px] min-w-[44px]`}>
                    <Icon name="delete" size="sm" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeView === 'quick-add' && (
          <div className="space-y-4">
            <div>
              <label className={`text-sm font-semibold ${tokens.text.primary} mb-2 block`}>Title *</label>
              <input
                type="text"
                className={`w-full px-3 py-2 rounded-landing text-sm ${tokens.border.light} border ${tokens.card.bg} ${tokens.text.primary} min-h-[44px]`}
                placeholder="Todo title"
              />
            </div>
            <div>
              <label className={`text-sm font-semibold ${tokens.text.primary} mb-2 block`}>Due Date</label>
              <input
                type="date"
                className={`w-full px-3 py-2 rounded-landing text-sm ${tokens.border.light} border ${tokens.card.bg} ${tokens.text.primary} min-h-[44px]`}
              />
            </div>
            <div>
              <label className={`text-sm font-semibold ${tokens.text.primary} mb-2 block`}>Priority</label>
              <select className={`w-full px-3 py-2 rounded-landing text-sm ${tokens.border.light} border ${tokens.card.bg} ${tokens.text.primary} min-h-[44px]`}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            <div>
              <label className={`text-sm font-semibold ${tokens.text.primary} mb-2 block`}>Assignee</label>
              <div className="flex gap-2 flex-wrap">
                <button className={`px-3 py-2 rounded-landing text-sm ${tokens.button.secondary} min-h-[44px]`}>
                  Family
                </button>
                {mockMembers.map(member => (
                  <button key={member.memberId} className={`px-3 py-2 rounded-landing text-sm ${tokens.button.secondary} min-h-[44px] flex items-center gap-2`}>
                    <MemberAvatar member={member} size="sm" />
                    {member.memberName}
                  </button>
                ))}
              </div>
            </div>
            <button className={`w-full px-4 py-3 rounded-landing ${tokens.button.primary} text-white font-semibold min-h-[48px]`}>
              Create Todo
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function MobileTodoRedesignProposals() {
  const { isDarkMode, toggleTheme } = useThemeStore();
  const tokens = getDesignTokens(isDarkMode);
  const [selectedProposal, setSelectedProposal] = useState<number | null>(null);

  const proposals = [
    {
      id: 1,
      name: 'Tabbed Interface with Quick Add',
      description: 'Separate tabs for Todos and Lists. Quick add form in header (desktop feature). Expandable filter panel with priority, assignee, and sort options.',
      component: () => <Proposal1FullUI isDarkMode={isDarkMode} />,
      features: [
        '✅ Quick add (title, due, priority) - Desktop feature',
        '✅ Priority filtering',
        '✅ Assignee filtering',
        '✅ Sort by due/priority/title',
        '✅ List management tab',
        '✅ Expandable filter panel',
      ],
      pros: [
        'All desktop features included',
        'Quick add saves time',
        'Comprehensive filtering',
        'Clear separation of concerns',
        'Touch-friendly (44px+ targets)',
      ],
      cons: [
        'Header can be tall when filters expanded',
        'Quick add takes header space',
        'More complex interface',
      ],
    },
    {
      id: 2,
      name: 'Drawer-Based Filters with View Toggle',
      description: 'Compact header with filter drawer (bottom sheet). Toggle between card and compact list views. All filters accessible via drawer.',
      component: () => <Proposal2FullUI isDarkMode={isDarkMode} />,
      features: [
        '✅ View toggle (cards/compact)',
        '✅ Filter drawer (bottom sheet)',
        '✅ Priority filtering',
        '✅ Sort options',
        '✅ Compact header',
        '✅ Maximum content visibility',
      ],
      pros: [
        'Minimal header footprint',
        'More screen space for todos',
        'Clean, uncluttered interface',
        'Filters hidden until needed',
        'View toggle for different use cases',
      ],
      cons: [
        'Filters require extra tap',
        'No quick add in header',
        'Less immediate access to features',
      ],
    },
    {
      id: 3,
      name: 'Swipeable Tabs with Dedicated Quick Add',
      description: 'Three swipeable tabs: Todos, Lists, Quick Add. Each tab optimized for its purpose. Quick Add tab includes all fields (desktop feature).',
      component: () => <Proposal3FullUI isDarkMode={isDarkMode} />,
      features: [
        '✅ Dedicated Quick Add tab',
        '✅ Full form in Quick Add (title, due, priority, assignee)',
        '✅ List management tab',
        '✅ Context-aware controls',
        '✅ Swipeable navigation',
      ],
      pros: [
        'Dedicated space for quick add',
        'Full feature parity with desktop',
        'Context-aware interface',
        'Swipeable navigation',
        'Clear separation of functions',
      ],
      cons: [
        'Requires tab switching',
        'More navigation needed',
        'Quick add not always visible',
      ],
    },
  ];

  return (
    <div className={`min-h-screen ${tokens.bg.gradient} transition-colors duration-200`}>
      <NavBar className={`${tokens.card.bg} ${tokens.border.light} border-b shadow-sm`} containerClassName="px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between w-full items-center">
          <h1 className={`text-xl font-bold ${tokens.text.primary}`}>Mobile Todo Complete Redesign Proposals</h1>
          <div className="flex items-center gap-3 sm:gap-4">
            <button
              onClick={toggleTheme}
              className={`${tokens.button.secondary} px-4 h-12 rounded-sm text-sm font-medium min-w-[100px] flex items-center justify-center gap-2 transition-all active:scale-95`}
            >
              <Icon 
                name={isDarkMode ? 'sun' : 'moon'} 
                size="md" 
                className={tokens.text.primary}
              />
              <span>{isDarkMode ? 'Light' : 'Dark'}</span>
            </button>
          </div>
        </div>
      </NavBar>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h2 className={`text-3xl font-bold ${tokens.text.primary} mb-4`}>
            Complete Mobile Todo Redesign - All Desktop Features
          </h2>
          <p className={`text-lg ${tokens.text.secondary} mb-2`}>
            Three comprehensive redesign proposals that include ALL features from the desktop version
          </p>
          <p className={`text-sm ${tokens.text.muted} mb-4`}>
            These proposals include: Quick Add, Priority Filtering, Assignee Filtering, Sorting, List Management, 
            View Toggles, and Event Linking - all adapted for mobile touch interfaces.
          </p>
          <div className={`${tokens.card.bg} ${tokens.border.light} border rounded-landing p-4 mb-6`}>
            <h3 className={`text-sm font-semibold ${tokens.text.primary} mb-2`}>Desktop Features Included:</h3>
            <ul className={`text-xs ${tokens.text.muted} space-y-1 list-disc list-inside`}>
              <li>✅ Quick Add form (title, due date, priority) - inline</li>
              <li>✅ Sortable columns (due, priority, title, assignee, list)</li>
              <li>✅ Priority filtering (critical, high, medium, low, all)</li>
              <li>✅ Assignee filtering (by family member)</li>
              <li>✅ List management (create, edit, delete)</li>
              <li>✅ View toggle (cards vs compact list)</li>
              <li>✅ Event linking (in create/edit modal)</li>
              <li>✅ Status filtering (open, done, all)</li>
              <li>✅ Scope filtering (my, family)</li>
            </ul>
          </div>
        </div>

        {/* Proposals */}
        <div className="space-y-8 mb-8">
          {proposals.map((proposal) => (
            <div
              key={proposal.id}
              className={`${tokens.card.bg} ${tokens.card.border} border-2 rounded-sm p-6 ${tokens.card.shadow} ${
                selectedProposal === proposal.id ? 'ring-2 ring-blue-500' : ''
              }`}
            >
              <div className="mb-4">
                <h3 className={`text-2xl font-bold ${tokens.text.primary} mb-2`}>
                  Proposal {proposal.id}: {proposal.name}
                </h3>
                <p className={`text-sm ${tokens.text.secondary} mb-4`}>
                  {proposal.description}
                </p>
              </div>

              {/* Features List */}
              <div className="mb-4">
                <h4 className={`text-sm font-semibold ${tokens.text.secondary} mb-2`}>
                  Features Included:
                </h4>
                <ul className={`text-xs ${tokens.text.muted} space-y-1 list-disc list-inside`}>
                  {proposal.features.map((feature, idx) => (
                    <li key={idx}>{feature}</li>
                  ))}
                </ul>
              </div>

              {/* Full UI Implementation */}
              <div className="mb-4">
                <h4 className={`text-sm font-semibold ${tokens.text.secondary} mb-3`}>
                  Full UI Implementation:
                </h4>
                <div className={`${tokens.bg.secondary} rounded-landing p-4 border-2 border-dashed`}>
                  {proposal.component()}
                </div>
              </div>

              {/* Pros/Cons */}
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                  <h5 className={`text-xs font-semibold ${tokens.text.primary} mb-2`}>Pros:</h5>
                  <ul className={`text-xs ${tokens.text.muted} space-y-1 list-disc list-inside`}>
                    {proposal.pros.map((pro, idx) => (
                      <li key={idx}>{pro}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h5 className={`text-xs font-semibold ${tokens.text.primary} mb-2`}>Cons:</h5>
                  <ul className={`text-xs ${tokens.text.muted} space-y-1 list-disc list-inside`}>
                    {proposal.cons.map((con, idx) => (
                      <li key={idx}>{con}</li>
                    ))}
                  </ul>
                </div>
              </div>

              <button
                onClick={() => setSelectedProposal(proposal.id === selectedProposal ? null : proposal.id)}
                className={`mt-4 w-full px-4 py-2 rounded-sm text-sm font-medium transition-all ${
                  selectedProposal === proposal.id
                    ? `${tokens.button.primary} text-white`
                    : `${tokens.button.secondary}`
                }`}
              >
                {selectedProposal === proposal.id ? 'Selected' : 'Select This Proposal'}
              </button>
            </div>
          ))}
        </div>

        {/* Comparison */}
        <div className={`${tokens.card.bg} ${tokens.card.border} border-2 rounded-sm p-6 ${tokens.card.shadow} mb-8`}>
          <h3 className={`text-2xl font-bold ${tokens.text.primary} mb-4`}>Feature Comparison</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={`${tokens.border.medium} border-b`}>
                  <th className={`text-left py-2 px-4 ${tokens.text.primary} font-semibold`}>Feature</th>
                  <th className={`text-left py-2 px-4 ${tokens.text.primary} font-semibold`}>Proposal 1</th>
                  <th className={`text-left py-2 px-4 ${tokens.text.primary} font-semibold`}>Proposal 2</th>
                  <th className={`text-left py-2 px-4 ${tokens.text.primary} font-semibold`}>Proposal 3</th>
                </tr>
              </thead>
              <tbody>
                <tr className={`${tokens.border.light} border-b`}>
                  <td className={`py-2 px-4 ${tokens.text.secondary} font-medium`}>Quick Add</td>
                  <td className={`py-2 px-4 ${tokens.text.muted}`}>✅ Header</td>
                  <td className={`py-2 px-4 ${tokens.text.muted}`}>❌</td>
                  <td className={`py-2 px-4 ${tokens.text.muted}`}>✅ Dedicated Tab</td>
                </tr>
                <tr className={`${tokens.border.light} border-b`}>
                  <td className={`py-2 px-4 ${tokens.text.secondary} font-medium`}>Priority Filter</td>
                  <td className={`py-2 px-4 ${tokens.text.muted}`}>✅ Expandable</td>
                  <td className={`py-2 px-4 ${tokens.text.muted}`}>✅ Drawer</td>
                  <td className={`py-2 px-4 ${tokens.text.muted}`}>✅</td>
                </tr>
                <tr className={`${tokens.border.light} border-b`}>
                  <td className={`py-2 px-4 ${tokens.text.secondary} font-medium`}>Assignee Filter</td>
                  <td className={`py-2 px-4 ${tokens.text.muted}`}>✅ Expandable</td>
                  <td className={`py-2 px-4 ${tokens.text.muted}`}>✅ Drawer</td>
                  <td className={`py-2 px-4 ${tokens.text.muted}`}>✅</td>
                </tr>
                <tr className={`${tokens.border.light} border-b`}>
                  <td className={`py-2 px-4 ${tokens.text.secondary} font-medium`}>Sorting</td>
                  <td className={`py-2 px-4 ${tokens.text.muted}`}>✅ Expandable</td>
                  <td className={`py-2 px-4 ${tokens.text.muted}`}>✅ Drawer</td>
                  <td className={`py-2 px-4 ${tokens.text.muted}`}>✅</td>
                </tr>
                <tr className={`${tokens.border.light} border-b`}>
                  <td className={`py-2 px-4 ${tokens.text.secondary} font-medium`}>List Management</td>
                  <td className={`py-2 px-4 ${tokens.text.muted}`}>✅ Tab</td>
                  <td className={`py-2 px-4 ${tokens.text.muted}`}>❌</td>
                  <td className={`py-2 px-4 ${tokens.text.muted}`}>✅ Tab</td>
                </tr>
                <tr className={`${tokens.border.light} border-b`}>
                  <td className={`py-2 px-4 ${tokens.text.secondary} font-medium`}>View Toggle</td>
                  <td className={`py-2 px-4 ${tokens.text.muted}`}>❌</td>
                  <td className={`py-2 px-4 ${tokens.text.muted}`}>✅ Cards/Compact</td>
                  <td className={`py-2 px-4 ${tokens.text.muted}`}>❌</td>
                </tr>
                <tr className={`${tokens.border.light} border-b`}>
                  <td className={`py-2 px-4 ${tokens.text.secondary} font-medium`}>Header Space</td>
                  <td className={`py-2 px-4 ${tokens.text.muted}`}>Large (expandable)</td>
                  <td className={`py-2 px-4 ${tokens.text.muted}`}>✅ Compact</td>
                  <td className={`py-2 px-4 ${tokens.text.muted}`}>Medium</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
